import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { phoneSchema } from '~constants/regexConst';
import { Account } from '~hooks/useAccount/models';
import { InviteType } from '~types/InviteTypes';

const primaryContactSchema = yup.object().shape({
  firstName: yup.string().required(t('form_validation_errors.required_simple')),
  lastName: yup.string().required(t('form_validation_errors.required_simple')),
  email: yup
    .string()
    .trim()
    .email(t('form_validation_errors.invalid_email'))
    .test(
      'require-email-based-on-selected-invite-types',
      t('form_validation_errors.required_simple'),
      (value, context) => {
        const inviteTypes = context.from?.[1]?.value?.inviteTypes;
        const isEmailInviteSelected = inviteTypes?.find((item: { value: InviteType }) => {
          return item.value === InviteType.EMAIL;
        });
        const isEmptyEmail = isNil(value) || String(value).trim().length === 0;

        if (isEmailInviteSelected && isEmptyEmail) {
          return false;
        }

        return true;
      },
    )
    .notRequired(),
  phone: phoneSchema
    .test(
      'require-phone-based-on-selected-invite-types',
      t('form_validation_errors.required_simple'),
      (value, context) => {
        const inviteTypes = context.from?.[1]?.value?.inviteTypes;
        const isSmsInviteSelected = inviteTypes?.find((item: { value: InviteType }) => {
          return item.value === InviteType.SMS;
        });
        const isEmptyPhone = isNil(value) || String(value).trim().length === 0;

        if (isSmsInviteSelected && isEmptyPhone) {
          return false;
        }

        return true;
      },
    )
    .notRequired(),
});

const billingContactSchema = yup.object().shape({
  firstName: yup.string().notRequired(),
  lastName: yup.string().notRequired(),
  email: yup
    .string()
    .trim()
    .email(t('form_validation_errors.invalid_email'))
    .notRequired(),
  phone: phoneSchema.notRequired(),
});

export const connectedAccountContactSchema = yup.object().shape({
  isEditing: yup.boolean().required(),
  primaryContact: primaryContactSchema.required(),
  isBillingContactSame: yup.boolean().required(),
  inviteTypes: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required(),
        value: yup.mixed<InviteType>().oneOf(Object.values(InviteType)),
      }),
    )
    .when('isEditing', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => {
        return schema.min(1, t('form_validation_errors.required_simple')).required();
      },
    }),
  billingContact: billingContactSchema.notRequired(),
});
export type ConnectedAccountContactDto = yup.InferType<
  typeof connectedAccountContactSchema
>;

function getContactNameFragments(contactName: string | undefined) {
  const fragments = contactName?.split(' ') || [];
  const firstName = fragments.slice(0, -1).join(' ');
  const lastName = fragments.slice(-1).join(' ');

  return { firstName, lastName };
}

export function setConnectedAccountContactSchemaDefaultValues(account?: Account) {
  const primaryContact = getContactNameFragments(account?.primaryContact?.name);
  const billingContact = getContactNameFragments(account?.billingContact?.name);

  const defaultValues: ConnectedAccountContactDto = {
    isEditing: Boolean(account?.id),
    primaryContact: {
      firstName: primaryContact.firstName,
      lastName: primaryContact.lastName,
      email: account?.primaryContact?.email ?? '',
      phone: account?.primaryContact?.phone ?? '',
    },
    isBillingContactSame: account?.isBillingContactSame ?? true,
    billingContact: {
      firstName: billingContact.firstName,
      lastName: billingContact.lastName,
      email: account?.billingContact?.email ?? '',
      phone: account?.billingContact?.phone ?? '',
    },
    inviteTypes: [],
  };

  return defaultValues;
}
